// Deals, read down the page.
//
// This was a kanban, and a kanban was the wrong shape. A pipeline is ONE
// dimensional — a sequence — so laying it out along the horizontal axis spends the
// screen's widest dimension on an ordering that needs almost none of it, and then
// hides half the stages behind a fold. Seven columns of 240px cannot show a deal's
// org, its value, its age and the demos behind it at the same time.
//
// So: the stages run DOWN the page in pipeline order, each with room for what it
// means, and each deal gets the full width of a row. The one thing that genuinely
// wants horizontal space — how the money is distributed across the pipeline — gets
// it, as a single funnel bar at the top.
//
// Dragging is gone. Moving a deal is a stage dropdown on its row: one click to open,
// one to choose, no aiming, and it works the same on a phone.

import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ArchivedToggle, Button, EmptyState, Section, Stats } from '../components/common'
import { DataTable, Num, type Column } from '../components/DataTable'
import { Icon } from '../components/Icon'
import { FilterInput, LensChip, View, type Lens } from '../components/Page'
import { TextMenu } from '../components/Picker'
import { useCreateEntity } from '../components/useCreate'
import { useStickyToggle, useStickyValue } from '../components/usePeek'
import type { Draft } from '../lib/events'
import { absoluteOf, money, moneyShort, relativeShort, todayIso } from '../lib/format'
import { daysSince, DORMANT_DEAL_DAYS, STALE_DEAL_DAYS } from '../lib/metrics'
import { isCommitted, stageTone, type Stage, type StageKind } from '../lib/model'
import {
  ALL_TIME,
  dealDate,
  inPeriod,
  parsePeriod,
  periodChoices,
  presetPeriods,
  type Period,
} from '../lib/period'
import {
  deliveryOf,
  entitiesOfKind,
  fieldStr,
  lastTouch,
  orgsForDeal,
  projectsForDeal,
  stageOf,
  titleOf,
  type EntityRecord,
} from '../lib/reducer'
import { useStore } from '../lib/store'

/** Open stages shade from pale to full ember as they advance, so the colour itself
 *  carries progress. Won and lost step out of that ramp entirely. */
function stageColor(stage: Stage, openIndex: number, openCount: number): string {
  // Committed and finished step out of the pipeline ramp entirely; `stageTone` is
  // the shared reading of what a stage kind means, so a chip elsewhere cannot
  // disagree with this table about what "won" looks like.
  const fixed = stageTone(stage.kind)
  if (fixed) return fixed
  // Mix toward the page ground rather than toward grey: same hue throughout, pale to
  // saturated, so the steps read as progress instead of as mud.
  const t = openCount <= 1 ? 1 : 0.34 + (openIndex / (openCount - 1)) * 0.66
  return `color-mix(in srgb, var(--ember) ${Math.round(t * 100)}%, var(--bg))`
}

/** The lenses over the deal table, by stage kind. */
const LENSES: { key: string; label: string; kinds: StageKind[] | null; color?: string }[] = [
  { key: 'all', label: 'All', kinds: null },
  { key: 'open', label: 'Pipeline', kinds: ['open'], color: 'ember' },
  { key: 'signed', label: 'Signed', kinds: ['won', 'delivery'], color: 'green' },
  { key: 'closed', label: 'Delivered', kinds: ['closed'], color: 'marine' },
  { key: 'lost', label: 'Lost', kinds: ['lost'], color: 'grey' },
]

export function DealsPage() {
  const { state, config, dispatch, version } = useStore()
  const navigate = useNavigate()
  const create = useCreateEntity()
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [collapsed, setCollapsed] = useState<Set<string>>(
    // Lost and delivered are history: kept, folded away until you want them.
    () =>
      new Set(
        config.stages.filter((s) => s.kind === 'lost' || s.kind === 'closed').map((s) => s.key),
      ),
  )
  const [showArchived, setShowArchived] = useState(false)
  const [lens, setLens] = useState('all')
  // Which period the NUMBERS cover. Deliberately not a lens: it never takes a deal
  // off the page, it only decides what the strip and the stage bar are adding up.
  // All time by default, so the page reads the same as it always has until asked.
  const [periodKey, setPeriodKey] = useStickyValue('crum.deals.period', ALL_TIME.key)
  const [query, setQuery] = useState('')
  // Flat table by default — it's the same sheet as every other view, and consistency
  // is the point. The stage sections stay one click away, and the choice sticks,
  // because whichever one you think in is not going to change day to day.
  const [grouped, setGrouped] = useStickyToggle('crum.deals.grouped', false)

  const period = parsePeriod(periodKey) ?? ALL_TIME
  const presets = presetPeriods()

  const { byStage, counted, archived, openTotal, wonTotal, openCount, wonCount, scope, choices } =
    useMemo(() => {
      void version
      const deals = entitiesOfKind(state, 'deal')
      const groups = new Map<string, EntityRecord[]>(config.stages.map((s) => [s.key, []]))
      for (const d of deals) groups.get(stageOf(d, config.stages))?.push(d)
      // Within a stage, the ones going stale float up — the list should nag.
      for (const list of groups.values()) {
        list.sort((a, b) => daysSince(lastTouch(state, b.id)) - daysSince(lastTouch(state, a.id)))
      }
      // The same map a second time, holding only what the period counts. TWO maps
      // rather than one filter at the point of use, because the sections below read
      // the first and the numbers above read the second — and they are allowed to
      // differ: a stage section sums the rows it is actually showing you.
      const inScope = new Map<string, EntityRecord[]>(config.stages.map((s) => [s.key, []]))
      const dates: (string | null)[] = []
      let undated = 0
      for (const d of deals) {
        const date = dealDate(d, config.stages)
        dates.push(date)
        if (!date) undated++
        if (!inPeriod(date, period)) continue
        inScope.get(stageOf(d, config.stages))?.push(d)
      }
      const sumWhere = (pred: (k: Stage['kind']) => boolean) =>
        config.stages
          .filter((s) => pred(s.kind))
          .flatMap((s) => inScope.get(s.key) ?? [])
          .reduce((acc, d) => acc + (Number(d.fields.value) || 0), 0)
      const countWhere = (pred: (k: Stage['kind']) => boolean) =>
        config.stages
          .filter((s) => pred(s.kind))
          .reduce((n, s) => n + (inScope.get(s.key)?.length ?? 0), 0)
      return {
        byStage: groups,
        counted: inScope,
        archived: entitiesOfKind(state, 'deal', true).filter((d) => d.archived),
        openTotal: sumWhere((k) => k === 'open'),
        // "Signed" is won PLUS in-delivery — both are committed money.
        wonTotal: sumWhere(isCommitted),
        openCount: countWhere((k) => k === 'open'),
        wonCount: countWhere(isCommitted),
        scope: {
          total: deals.length,
          inRange: [...inScope.values()].reduce((n, list) => n + list.length, 0),
          undated,
        },
        choices: periodChoices(dates),
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [version, config, periodKey])

  const openStages = config.stages.filter((s) => s.kind === 'open')
  const total = entitiesOfKind(state, 'deal').length

  const kindOfDeal = (d: EntityRecord) =>
    config.stages.find((s) => s.key === stageOf(d, config.stages))?.kind
  const inLens = (d: EntityRecord, key: string) => {
    const kinds = LENSES.find((l) => l.key === key)?.kinds
    if (!kinds) return true
    const k = kindOfDeal(d)
    return !!k && kinds.includes(k)
  }
  const lenses: Lens[] = LENSES.map((l) => ({
    key: l.key,
    label: l.label,
    color: l.color,
    count: entitiesOfKind(state, 'deal').filter((d) => inLens(d, l.key)).length,
  })).filter((l) => l.key === 'all' || (l.count ?? 0) > 0)

  // The rows for the flat table, and the same filter applied to the stage sections,
  // so switching shape never changes which deals you're looking at.
  const q = query.trim().toLowerCase()
  const visible = (list: EntityRecord[]) =>
    list.filter(
      (d) =>
        inLens(d, lens) &&
        (!q ||
          titleOf(state, d.id).toLowerCase().includes(q) ||
          titleOf(state, fieldStr(d, 'org')).toLowerCase().includes(q)),
    )
  const rows = visible(entitiesOfKind(state, 'deal'))

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <View
      lenses={lenses}
      active={lens}
      onLens={setLens}
      actions={
        <>
          <ShapeToggle grouped={grouped} onChange={setGrouped} />
          <FilterInput value={query} onChange={setQuery} />
          <ArchivedToggle
            count={archived.length}
            on={showArchived}
            onToggle={() => setShowArchived((v) => !v)}
          />
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Icon name="Plus" size={14} /> New
          </Button>
        </>
      }
    >
      {creating && (
        <div
          className="rounded-control mb-5 flex items-center gap-2 border p-2"
          style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
        >
          <Icon name="Handshake" size={16} style={{ color: 'var(--ember)' }} />
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) {
                void create('deal', name)
                setName('')
                setCreating(false)
              }
              if (e.key === 'Escape') setCreating(false)
            }}
            onBlur={() => setCreating(false)}
            placeholder="Deal name"
            className="text-body flex-1 bg-transparent outline-none"
          />
          <span className="meta">Enter to create</span>
        </div>
      )}

      {total === 0 ? (
        <EmptyState
          icon="Handshake"
          title="No deals yet"
          hint="A deal is one record from idea through delivered."
          action={
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Icon name="Plus" size={14} /> New deal
            </Button>
          }
        />
      ) : (
        <>
          <Funnel
            stages={openStages}
            byStage={counted}
            openTotal={openTotal}
            openCount={openCount}
            wonTotal={wonTotal}
            wonCount={wonCount}
            period={period}
            presets={presets}
            choices={choices}
            onPeriod={setPeriodKey}
            scope={scope}
          />

          <div className="mt-8">
            {grouped ? (
              config.stages.map((stage, i) => {
                const openIndex = openStages.findIndex((s) => s.key === stage.key)
                return (
                  <StageSection
                    key={stage.key}
                    stage={stage}
                    color={stageColor(stage, openIndex < 0 ? i : openIndex, openStages.length)}
                    deals={visible(byStage.get(stage.key) ?? [])}
                    collapsed={collapsed.has(stage.key)}
                    onToggle={() => toggle(stage.key)}
                  />
                )
              })
            ) : (
              <DealTable rows={rows} onOpen={(id) => navigate(`/e/${id}`)} />
            )}
          </div>

          {showArchived && archived.length > 0 && (
            <Section title="Archived" note={`${archived.length}`}>
              <div className="rows">
                {archived.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => navigate(`/e/${d.id}`)}
                    className="hatched flex w-full items-center gap-3 py-2.5 text-left"
                  >
                    <span className="text-body min-w-0 flex-1 truncate font-medium">
                      {titleOf(state, d.id) || 'Untitled deal'}
                    </span>
                    <span
                      className="num"
                      style={{ fontSize: 'var(--text-meta)', color: 'var(--faint)' }}
                    >
                      {money(Number(d.fields.value) || 0, '·')}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        void dispatch({ type: 'deal.restore', subject: d.id })
                      }}
                      className="text-label font-medium"
                      style={{ color: 'var(--accent)' }}
                    >
                      Restore
                    </button>
                  </button>
                ))}
              </div>
            </Section>
          )}
        </>
      )}
    </View>
  )
}

/**
 * The deal sheet. Same table as every other view, so the columns answer the
 * comparative questions: which of these is worth the most, which is going cold,
 * what is owed on the ones we've signed.
 *
 * The stage cell is the live control rather than a readout — moving a deal is the
 * most common edit on this page, and making it possible without opening the record
 * is most of what a pipeline view is for.
 */
function DealTable({ rows, onOpen }: { rows: EntityRecord[]; onOpen: (id: string) => void }) {
  const { state, config, version } = useStore()
  const navigate = useNavigate()
  const move = useMoveDeal()

  const columns = useMemo<Column<EntityRecord>[]>(() => {
    void version
    const order = (d: EntityRecord) =>
      config.stages.findIndex((s) => s.key === stageOf(d, config.stages))
    return [
      {
        // Name first: a pill in the leading column reads like a bullet and pushes the
        // thing you're actually scanning for into second place.
        key: 'title',
        label: 'Deal',
        value: (d) => titleOf(state, d.id),
        render: (d) => {
          const orgs = orgsForDeal(state, d.id)
          const orgLine = orgs.map((o) => titleOf(state, o.id)).join(', ')
          const named = orgLine.toLowerCase() === titleOf(state, d.id).trim().toLowerCase()
          return (
            <span className="min-w-0">
              <span className="block truncate font-medium">
                {titleOf(state, d.id) || 'Untitled deal'}
              </span>
              {orgLine && !named && <span className="sub block truncate">{orgLine}</span>}
            </span>
          )
        },
      },
      {
        key: 'stage',
        label: 'Stage',
        width: 138,
        value: (d) => order(d),
        render: (d) => (
          // The picker is a control inside a clickable row: swallow the click so
          // changing a stage doesn't also open the peek behind the menu.
          <span onClick={(e) => e.stopPropagation()} className="inline-block">
            <StagePicker value={stageOf(d, config.stages)} onPick={(to) => move(d, to)} />
          </span>
        ),
      },
      {
        // One column: the work associated with this deal. It used to be split into
        // "supports" and "produces" chips, a distinction the work's own kind already
        // makes and that nobody read the same way twice.
        key: 'works',
        label: 'Projects',
        width: 220,
        hideBelow: 1024,
        value: (d) => projectsForDeal(state, d.id).length,
        render: (d) => {
          const items = projectsForDeal(state, d.id)
          if (items.length === 0) return <span style={{ color: 'var(--faint)' }}>·</span>
          return (
            <span className="flex flex-wrap gap-1">
              {items.slice(0, 2).map((w) => (
                <span
                  key={w.id}
                  className="rounded-inline inline-flex max-w-[11rem] items-center gap-1 truncate px-1.5 py-0.5"
                  style={{
                    background: 'var(--work-soft)',
                    color: 'var(--marine)',
                    fontFamily: 'var(--mono)',
                    fontSize: 'var(--text-micro)',
                  }}
                >
                  {titleOf(state, w.id)}
                </span>
              ))}
              {items.length > 2 && <span className="meta">+{items.length - 2}</span>}
            </span>
          )
        },
      },
      {
        key: 'value',
        label: 'Value',
        align: 'right',
        width: 100,
        value: (d) => Number(d.fields.value) || 0,
        render: (d) => <Num value={money(Number(d.fields.value) || 0, '·')} hue="--ember" />,
      },
      {
        // What this deal owes next. Sorted so undated and finished deals fall to the
        // bottom rather than to the top.
        key: 'due',
        label: 'Owed',
        align: 'right',
        width: 112,
        hideBelow: 672,
        value: (d) => deliveryOf(state, d.id).next?.due ?? 'zzzz',
        render: (d) => {
          const del = deliveryOf(state, d.id)
          if (del.items.length === 0) return <span style={{ color: 'var(--faint)' }}>·</span>
          const late = del.next?.due ? daysSince(del.next.due) > 0 : false
          return (
            <span
              className="num"
              style={{
                fontSize: 'var(--text-label)',
                color: del.owed === 0 ? 'var(--good)' : late ? 'var(--crit)' : 'var(--warn)',
              }}
              title={`${del.delivered} of ${del.items.length} delivered${del.next ? ` · next: ${del.next.label}` : ''}`}
            >
              {del.owed === 0 ? `all ${del.items.length}` : `${del.delivered}/${del.items.length}`}
              {del.next?.due ? ` · ${del.next.due.slice(5)}` : ''}
            </span>
          )
        },
      },
      {
        // Going cold used to be a "20d cold" pill floating after the deal's name, which
        // read as a badge on the title rather than as an age. It's the same fact this
        // column already shows, so the column says it: the age turns red once an OPEN
        // deal passes the threshold. A signed or finished deal is never "cold".
        key: 'touched',
        label: 'Touched',
        align: 'right',
        width: 96,
        hideBelow: 448,
        value: (d) => lastTouch(state, d.id),
        render: (d) => {
          const age = daysSince(lastTouch(state, d.id))
          const open =
            config.stages.find((s) => s.key === stageOf(d, config.stages))?.kind === 'open'
          // Cold is a window, not a half-line: an open deal that has been quiet for
          // a fortnight is a thing to do this week, and one that has been quiet for
          // two months is history. Only the first wears the warning colour.
          const stale = open && age >= STALE_DEAL_DAYS && age < DORMANT_DEAL_DAYS
          const dormant = open && age >= DORMANT_DEAL_DAYS
          return (
            <span
              className="num"
              style={{
                fontSize: 'var(--text-label)',
                color: stale ? 'var(--crit)' : 'var(--faint)',
                fontWeight: stale ? 550 : 400,
              }}
              title={`Last touch ${absoluteOf(lastTouch(state, d.id))} · ${age} days ago`}
            >
              {/* A cold row shows exact DAYS, not "2w ago". Two rows can both round to
                  "2w" with only one past the threshold, and then the colour looks
                  arbitrary; the precise number is the explanation. */}
              {stale || dormant ? `${age}d cold` : relativeShort(lastTouch(state, d.id))}
            </span>
          )
        },
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, config])

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(d) => d.id}
      onOpen={(d) => onOpen(d.id)}
      onOpenFull={(d) => navigate(`/e/${d.id}`)}
      openLabel={(d) => titleOf(state, d.id)}
      storageKey="crum.deals.sort"
      initialSort={{ key: 'stage', dir: 'asc' }}
      empty={<p className="meta py-6">Nothing in this lens.</p>}
    />
  )
}

/** Table or stages. Two shapes for one list, and the choice is remembered. */
function ShapeToggle({ grouped, onChange }: { grouped: boolean; onChange: (v: boolean) => void }) {
  const opts = [
    { on: false, icon: 'Table2' as const, label: 'Table' },
    { on: true, icon: 'Layers' as const, label: 'Stages' },
  ]
  return (
    <span
      className="rounded-control flex items-center gap-0.5 border p-0.5"
      style={{ borderColor: 'var(--border)' }}
    >
      {opts.map((o) => (
        <button
          key={o.label}
          type="button"
          onClick={() => onChange(o.on)}
          title={`Group by ${o.on ? 'stage' : 'nothing'}`}
          aria-pressed={grouped === o.on}
          className="rounded-inline flex h-6 w-7 items-center justify-center"
          style={{
            background: grouped === o.on ? 'var(--hover)' : 'transparent',
            color: grouped === o.on ? 'var(--fg)' : 'var(--faint)',
          }}
        >
          <Icon name={o.icon} size={14} />
        </button>
      ))}
    </span>
  )
}

/**
 * Moving a deal. Reaching a committed stage is what turns it into a contract, so the
 * signed date gets stamped once here rather than being a second thing to remember.
 * Shared by the table cell and the stage-view row so both behave identically.
 */
function useMoveDeal() {
  const { config, dispatch } = useStore()
  return (deal: EntityRecord, to: string) => {
    const current = stageOf(deal, config.stages)
    if (to === current) return
    const drafts: Draft[] = [
      { type: 'deal.stage_change', subject: deal.id, data: { from: current, to } },
    ]
    if (isCommitted(config.stages.find((s) => s.key === to)?.kind) && !fieldStr(deal, 'signedAt')) {
      drafts.push({ type: 'deal.update', subject: deal.id, data: { signedAt: todayIso() } })
    }
    void dispatch(drafts)
  }
}

/**
 * The whole pipeline in one bar. This is the one thing on the page that genuinely
 * wants the horizontal axis: the shape of where money is sitting.
 *
 * Sized by value — except when nothing has a value yet, where it falls back to
 * counts and says so, rather than rendering a row of invisible segments.
 */
function Funnel({
  stages,
  byStage,
  openTotal,
  openCount,
  wonTotal,
  wonCount,
  period,
  presets,
  choices,
  onPeriod,
  scope,
}: {
  stages: Stage[]
  byStage: Map<string, EntityRecord[]>
  openTotal: number
  openCount: number
  wonTotal: number
  wonCount: number
  period: Period
  presets: Period[]
  choices: Period[]
  onPeriod: (key: string) => void
  scope: { total: number; inRange: number; undated: number }
}) {
  const rows = stages.map((s, i) => {
    const deals = byStage.get(s.key) ?? []
    return {
      stage: s,
      count: deals.length,
      value: deals.reduce((acc, d) => acc + (Number(d.fields.value) || 0), 0),
      color: stageColor(s, i, stages.length),
    }
  })
  const byValue = openTotal > 0
  const denom = byValue ? openTotal : openCount

  const deals = (n: number) => `${n} ${n === 1 ? 'deal' : 'deals'}`

  return (
    <section>
      <PeriodBar
        period={period}
        presets={presets}
        choices={choices}
        onPeriod={onPeriod}
        scope={scope}
      />

      {/* The same strip Overview draws, rather than the third hand-rolled one that
          stood here. The sub-line is what the third one existed for, and `Stats`
          carries it now. */}
      <Stats
        items={[
          {
            label: 'Pipeline',
            value: money(openTotal, '·'),
            sub: deals(openCount),
            hue: '--ember',
          },
          { label: 'Signed', value: money(wonTotal, '·'), sub: deals(wonCount), hue: '--good' },
        ]}
      />

      <div className="sechead mb-2">
        <h2 className="text-body font-semibold">By stage</h2>
        {/* "no values set yet" is only true when there ARE open deals and none of
            them carries a number. A period with nothing open at all says so on its
            own line below, and claiming the app has no values because this quarter
            is empty would be a lie about the other three. */}
        <span className="meta ml-auto">
          {[
            period.from ? period.label : '',
            byValue ? 'by value' : openCount > 0 ? 'by count · no values set yet' : '',
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </div>

      {denom > 0 && (
        <>
          <div className="flex h-2.5 w-full gap-[2px] overflow-hidden rounded-full">
            {rows.map((r) => {
              const share = ((byValue ? r.value : r.count) / denom) * 100
              if (share <= 0) return null
              return (
                <span
                  key={r.stage.key}
                  title={`${r.stage.label} · ${r.count} · ${money(r.value, 'no value set')}`}
                  style={{ width: `${share}%`, background: r.color, minWidth: 3 }}
                />
              )
            })}
          </div>
          {/* A legend, but only for segments wide enough to be worth naming. */}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {rows
              .filter((r) => r.count > 0)
              .map((r) => (
                <span key={r.stage.key} className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ background: r.color }} />
                  <span className="text-meta">{r.stage.label}</span>
                  <span
                    className="num"
                    style={{ fontSize: 'var(--text-label)', color: 'var(--faint)' }}
                  >
                    {moneyShort(r.value, String(r.count))}
                  </span>
                </span>
              ))}
          </div>
        </>
      )}

      {denom === 0 && (
        <p className="meta" style={{ color: 'var(--faint)' }}>
          {period.from ? `Nothing open in ${period.label}.` : 'Nothing open.'}
        </p>
      )}
    </section>
  )
}

/**
 * The period chips, over the numbers they scope.
 *
 * `LensChip` because it is the app's one chip and this narrows rather than
 * navigates — but it sits HERE rather than in the view bar beside the lenses,
 * because the lenses decide which deals you are looking at and this decides
 * nothing of the kind. Putting the two rows together would have said they were
 * the same sort of control, and the first question anyone asked would have been
 * why the list did not get shorter.
 *
 * Four chips is the budget. Everything else is one menu behind them, holding only
 * the years and quarters the deals actually reach; the presets are left out of it
 * so exactly one of the two controls is ever lit.
 */
function PeriodBar({
  period,
  presets,
  choices,
  onPeriod,
  scope,
}: {
  period: Period
  presets: Period[]
  choices: Period[]
  onPeriod: (key: string) => void
  scope: { total: number; inRange: number; undated: number }
}) {
  const fromMenu = choices.some((c) => c.key === period.key) ? period.key : ''

  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1.5">
      <span className="eyebrow" style={{ fontSize: 'var(--text-micro)' }}>
        Counting
      </span>
      {presets.map((p) => (
        <LensChip
          key={p.key}
          lens={{ key: p.key, label: p.label }}
          active={p.key === period.key}
          onPick={onPeriod}
        />
      ))}
      {choices.length > 0 && (
        <TextMenu
          label="Another period"
          value={fromMenu}
          empty="More"
          chevron
          options={choices.map((c) => ({ key: c.key, label: c.label }))}
          onPick={onPeriod}
        />
      )}
      {/* What the period LEFT OUT, which is the number a scoped total owes you. A
          deal with no date on it cannot be inside a range or outside one, and most
          deals have no date early on, so it is said separately rather than folded
          into the ones that simply fall elsewhere. */}
      {period.from && (
        <span className="meta ml-auto">
          {scope.inRange} of {scope.total} deals in {period.label}
          {scope.undated > 0 ? ` · ${scope.undated} with no date` : ''}
        </span>
      )}
    </div>
  )
}

function StageSection({
  stage,
  color,
  deals,
  collapsed,
  onToggle,
}: {
  stage: Stage
  color: string
  deals: EntityRecord[]
  collapsed: boolean
  onToggle: () => void
}) {
  const value = deals.reduce((acc, d) => acc + (Number(d.fields.value) || 0), 0)
  const empty = deals.length === 0

  // An empty stage still belongs in the sequence — you need to see the gap — but it
  // gets one slim line rather than a heading, a description and an apology.
  if (empty) {
    return (
      <div
        className="mb-2 flex items-center gap-2.5 border-b pb-2"
        style={{ borderColor: 'var(--rule-soft)' }}
        title={stage.description}
      >
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ background: color, opacity: 0.5 }}
        />
        <span className="text-body" style={{ color: 'var(--faint)' }}>
          {stage.label}
        </span>
        <span className="meta ml-auto" style={{ color: 'var(--faint)' }}>
          empty
        </span>
      </div>
    )
  }

  return (
    <section className="mb-7">
      {/* Sticky so you always know which stage you're reading, however long it runs. */}
      <div
        className="ground sticky z-10 -mx-1 px-1 pt-1 pb-2"
        style={{ backgroundColor: 'var(--bg)', top: 'var(--page-header-h)' }}
      >
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-center gap-2.5 border-b pb-2 text-left"
          style={{ borderColor: 'var(--border)' }}
        >
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} />
          <h2 className="text-lead font-semibold tracking-tight">{stage.label}</h2>
          <span className="meta">{deals.length}</span>
          {value > 0 && (
            <span className="num" style={{ fontSize: 'var(--text-body)', color: 'var(--ember)' }}>
              {money(value)}
            </span>
          )}
          <Icon
            name={collapsed ? 'ChevronRight' : 'ChevronDown'}
            size={14}
            className="ml-auto"
            style={{ color: 'var(--faint)' }}
          />
        </button>
      </div>

      {/* Indented under the heading, same as every other section in the app: it's
          what makes a heading read as the parent of what follows. */}
      {!collapsed && (
        <div className="pl-3.5 sm:pl-5">
          {stage.description && (
            <p className="text-body mt-2 mb-1 max-w-[64ch]" style={{ color: 'var(--muted)' }}>
              {stage.description}
            </p>
          )}
          <div className="rows mt-1">
            {deals.map((d) => (
              <DealRow key={d.id} deal={d} />
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

function DealRow({ deal }: { deal: EntityRecord }) {
  const { state, config } = useStore()
  const move = useMoveDeal()
  const navigate = useNavigate()
  const current = stageOf(deal, config.stages)
  const stage = config.stages.find((s) => s.key === current)
  const orgs = orgsForDeal(state, deal.id)
  const works = projectsForDeal(state, deal.id)
  const signed = fieldStr(deal, 'signedAt')
  const age = daysSince(lastTouch(state, deal.id))
  const stale = stage?.kind === 'open' && age >= STALE_DEAL_DAYS

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 py-3">
      <button
        type="button"
        onClick={() => navigate(`/e/${deal.id}`)}
        className="min-w-[14rem] flex-1 text-left"
      >
        <span className="text-body block truncate font-medium">
          {titleOf(state, deal.id) || 'Untitled deal'}
        </span>
        <span className="meta mt-0.5 block truncate">
          {orgs.length > 0 && <>{orgs.map((o) => titleOf(state, o.id)).join(', ')} · </>}
          {signed ? (
            `signed ${signed}`
          ) : Number.isFinite(age) ? (
            // Same rule as the table: the age itself goes red rather than growing a badge.
            <span
              style={stale ? { color: 'var(--crit)', fontWeight: 550 } : undefined}
              title={`Last touch ${age} days ago`}
            >
              {stale ? `cold ${age} days` : `touched ${relativeShort(lastTouch(state, deal.id))}`}
            </span>
          ) : null}
        </span>
      </button>

      {/* The work on this deal. Full width means these fit on the row instead of
          being cropped off a kanban card. */}
      {works.length > 0 && (
        <span className="flex flex-wrap items-center gap-1">
          {works.map((w) => (
            <button
              key={w.id}
              type="button"
              onClick={() => navigate(`/e/${w.id}`)}
              className="rounded-inline inline-flex max-w-[13rem] items-center gap-1 truncate px-1.5 py-0.5"
              style={{
                background: 'var(--work-soft)',
                color: 'var(--marine)',
                fontFamily: 'var(--mono)',
                fontSize: 'var(--text-micro)',
              }}
            >
              <Icon name="Boxes" size={12} /> {titleOf(state, w.id)}
            </button>
          ))}
        </span>
      )}

      <span
        className="num w-24 shrink-0 text-right"
        style={{ fontSize: 'var(--text-body)', color: 'var(--ember)' }}
      >
        {money(Number(deal.fields.value) || 0, '')}
      </span>

      {/* Moving a deal: a dropdown, not a drag. */}
      <StagePicker value={current} onPick={(to) => move(deal, to)} />
    </div>
  )
}

/**
 * The stage control. Deliberately its own component rather than the shared
 * PillSelect: it shows each stage's description in the menu, which is where the
 * question "wait, what counts as Qualified?" actually gets asked.
 */
function StagePicker({ value, onPick }: { value: string; onPick: (key: string) => void }) {
  const { config } = useStore()
  const [open, setOpen] = useState(false)
  // The picker sits at the LEFT edge of a table row and at the right edge of a stage
  // row, so the menu can't be pinned to one side. Measure on open and hang it off
  // whichever side has 19rem of room.
  const btn = useRef<HTMLButtonElement | null>(null)
  const [flip, setFlip] = useState(false)
  const openStages = config.stages.filter((s) => s.kind === 'open')
  const current = config.stages.find((s) => s.key === value)
  const idx = openStages.findIndex((s) => s.key === value)
  const color = current ? stageColor(current, idx < 0 ? 0 : idx, openStages.length) : 'var(--muted)'

  return (
    <span className="relative shrink-0">
      <button
        ref={btn}
        type="button"
        onClick={() => {
          const r = btn.current?.getBoundingClientRect()
          if (r) setFlip(r.left + 19 * 16 > window.innerWidth)
          setOpen((v) => !v)
        }}
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 'var(--text-micro)',
          letterSpacing: '0.03em',
          color,
          background: `color-mix(in srgb, ${color} 13%, transparent)`,
          border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
        }}
      >
        {current?.label ?? 'unstaged'}
        <Icon name="ChevronDown" size={12} />
      </button>

      {open && (
        <>
          {/* Click-away catcher: cheaper and more reliable than a document listener,
              and it can't fight the menu's own scrolling. */}
          <span className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <ul
            className={`absolute ${flip ? 'right-0' : 'left-0'} rounded-float top-full z-50 mt-1 w-[19rem] overflow-auto border py-1 shadow-lg`}
            style={{
              background: 'var(--panel)',
              borderColor: 'var(--border)',
              maxHeight: 340,
              overscrollBehavior: 'contain',
            }}
          >
            {config.stages.map((s, i) => {
              const oi = openStages.findIndex((x) => x.key === s.key)
              const c = stageColor(s, oi < 0 ? i : oi, openStages.length)
              return (
                <li key={s.key}>
                  <button
                    type="button"
                    onClick={() => {
                      onPick(s.key)
                      setOpen(false)
                    }}
                    className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-[var(--hover)]"
                  >
                    <span
                      className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                      style={{ background: c }}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="text-body font-medium">{s.label}</span>
                        {s.key === value && (
                          <Icon name="Check" size={12} style={{ color: 'var(--muted)' }} />
                        )}
                      </span>
                      {s.description && (
                        <span
                          className="text-meta mt-0.5 block leading-snug"
                          style={{ color: 'var(--muted)' }}
                        >
                          {s.description}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </>
      )}
    </span>
  )
}
